/**
 * EmailDuplicatesPanel — list volunteers grouped by shared email and
 * let the operator quickly link them as a family.
 *
 * Backend: GET /api/volunteers/email-duplicates → groups with count>=2.
 *
 * Family states (badge color):
 *   - "none"     : ninguno enlazado            (red)
 *   - "partial"  : algunos linkados            (amber)
 *   - "complete" : todos comparten familia     (emerald)
 *   - "conflict" : >1 family_id distintas      (red + warning)
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Envelope, CaretDown, CaretRight, Link as LinkIcon,
  Warning, CheckCircle, X, MagnifyingGlass,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  none: { es: 'Sin vincular', en: 'Unlinked', cls: 'bg-red-50 text-red-700 border-red-200' },
  partial: { es: 'Vinculacion parcial', en: 'Partial', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  complete: { es: 'Familia completa', en: 'Complete', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  conflict: { es: 'Conflicto - revisar', en: 'Conflict - review', cls: 'bg-red-100 text-red-800 border-red-300' },
};

function ageFromDOB(dob) {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 86400 * 1000));
  } catch { return null; }
}

export default function EmailDuplicatesPanel({ lang, onClose }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [manageGroup, setManageGroup] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/volunteers/email-duplicates`,
        { withCredentials: true },
      );
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al cargar' : 'Load error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g =>
      g.email.includes(q) ||
      g.volunteers.some(v =>
        (v.display_name || '').toLowerCase().includes(q) ||
        (v.dni || '').toLowerCase().includes(q) ||
        (v.volunteer_id || '').toLowerCase().includes(q)
      )
    );
  }, [groups, search]);

  const counts = useMemo(() => {
    const out = { none: 0, partial: 0, complete: 0, conflict: 0 };
    groups.forEach(g => { out[g.family_status] = (out[g.family_status] || 0) + 1; });
    return out;
  }, [groups]);

  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Envelope size={18} className="text-[#1f3e80]" weight="duotone" />
          <h2 className="text-sm font-semibold text-stone-800">
            {lang === 'es' ? 'Voluntarios con el mismo email' : 'Volunteers sharing email'}
          </h2>
          <Badge variant="outline" className="text-[10px]">{groups.length}</Badge>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <Badge variant="outline" className={STATUS_LABEL.none.cls}>{lang === 'es' ? 'Sin vincular' : 'Unlinked'}: {counts.none}</Badge>
          <Badge variant="outline" className={STATUS_LABEL.partial.cls}>{lang === 'es' ? 'Parcial' : 'Partial'}: {counts.partial}</Badge>
          <Badge variant="outline" className={STATUS_LABEL.complete.cls}>{lang === 'es' ? 'Completa' : 'Complete'}: {counts.complete}</Badge>
          {counts.conflict > 0 && <Badge variant="outline" className={STATUS_LABEL.conflict.cls}>{lang === 'es' ? 'Conflictos' : 'Conflicts'}: {counts.conflict}</Badge>}
        </div>
        <div className="flex-1" />
        <div className="relative w-64">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === 'es' ? 'Filtrar email, nombre, DNI...' : 'Filter email, name, DNI...'}
            className="pl-8 h-8 text-xs"
            data-testid="email-dup-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onClose} data-testid="email-dup-close">
          <X size={14} className="mr-1" /> {lang === 'es' ? 'Volver al listado' : 'Back to list'}
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center">
          <Envelope size={36} weight="duotone" className="text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500">{lang === 'es' ? 'Sin grupos con email duplicado' : 'No groups with shared email'}</p>
        </div>
      ) : (
        <div className="divide-y divide-stone-200">
          {filtered.map(g => (
            <GroupRow
              key={g.email}
              group={g}
              expanded={!!expanded[g.email]}
              onToggle={() => setExpanded(p => ({ ...p, [g.email]: !p[g.email] }))}
              onManage={() => setManageGroup(g)}
              lang={lang}
            />
          ))}
        </div>
      )}

      <ManageFamilyDialog
        group={manageGroup}
        onClose={() => setManageGroup(null)}
        onSaved={() => { setManageGroup(null); load(); }}
        lang={lang}
      />
    </div>
  );
}


function GroupRow({ group, expanded, onToggle, onManage, lang }) {
  const status = STATUS_LABEL[group.family_status] || STATUS_LABEL.none;
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center gap-3 cursor-pointer" onClick={onToggle}>
        {expanded ? <CaretDown size={14} className="text-stone-500" /> : <CaretRight size={14} className="text-stone-500" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-stone-900 truncate">{group.email}</span>
            <Badge variant="outline" className="text-[10px]">{group.count} {lang === 'es' ? 'voluntarios' : 'volunteers'}</Badge>
            <Badge variant="outline" className={`text-[10px] ${status.cls}`}>
              {group.family_status === 'conflict' && <Warning size={10} weight="fill" className="inline mr-0.5" />}
              {group.family_status === 'complete' && <CheckCircle size={10} weight="fill" className="inline mr-0.5" />}
              {lang === 'es' ? status.es : status.en}
            </Badge>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="text-xs"
          onClick={(e) => { e.stopPropagation(); onManage(); }}
          data-testid={`email-dup-manage-${group.email}`}
        >
          <LinkIcon size={12} className="mr-1" />
          {lang === 'es' ? 'Gestionar familia' : 'Manage family'}
        </Button>
      </div>

      {expanded && (
        <div className="mt-2 ml-6 border-l-2 border-stone-100 pl-3">
          <table className="w-full text-xs">
            <thead className="text-stone-500">
              <tr className="border-b border-stone-100">
                <th className="text-left py-1.5 font-medium">V-code</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Nombre' : 'Name'}</th>
                <th className="text-left font-medium">DNI</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Tel' : 'Phone'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Edad' : 'Age'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Estado' : 'Status'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Familia' : 'Family'}</th>
              </tr>
            </thead>
            <tbody>
              {group.volunteers.map(v => {
                const age = ageFromDOB(v.fecha_nacimiento);
                return (
                  <tr key={v.volunteer_id} className="border-b border-stone-50 last:border-0">
                    <td className="py-1.5 font-mono text-[10px]">
                      <a href={`/volunteers/${v.volunteer_id}`} target="_blank" rel="noreferrer" className="text-[#1f3e80] hover:underline">
                        {v.volunteer_id}
                      </a>
                    </td>
                    <td>{v.display_name}</td>
                    <td className="font-mono text-[10px]">{v.dni || '-'}</td>
                    <td>{v.telefono || '-'}</td>
                    <td>{age !== null ? age : '-'}</td>
                    <td><Badge variant="outline" className="text-[9px]">{v.status || '-'}</Badge></td>
                    <td>
                      {v.family_id ? (
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          {v.family_role || 'miembro'}
                          {v.family_relationship && ` · ${v.family_relationship}`}
                        </Badge>
                      ) : (
                        <span className="text-stone-400 text-[10px]">{lang === 'es' ? 'sin familia' : 'no family'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ManageFamilyDialog({ group, onClose, onSaved, lang }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!group) return;
    // Smart defaults: oldest adult (>=18) = titular, rest dependiente.
    const adults = group.volunteers
      .map(v => ({ ...v, _age: ageFromDOB(v.fecha_nacimiento) }))
      .filter(v => v._age !== null && v._age >= 18)
      .sort((a, b) => b._age - a._age);
    const titularId = adults[0]?.volunteer_id;
    setRows(group.volunteers.map(v => ({
      ...v,
      _role: v.family_role || (v.volunteer_id === titularId ? 'titular' : 'dependiente'),
      _relationship: v.family_relationship || '',
    })));
  }, [group]);

  if (!group) return null;
  const isConflict = group.family_status === 'conflict';

  const update = (vid, field, value) => {
    setRows(p => p.map(r => r.volunteer_id === vid ? { ...r, [field]: value } : r));
  };

  const submit = async () => {
    // Pick the first titular as the base of the family
    const titular = rows.find(r => r._role === 'titular');
    if (!titular) {
      toast.error(lang === 'es' ? 'Necesitas al menos un titular' : 'You need at least one holder');
      return;
    }
    setSaving(true);
    try {
      // Link each non-titular member against the titular. If already linked,
      // we send anyway — the backend handles same-family case gracefully.
      for (const r of rows) {
        if (r.volunteer_id === titular.volunteer_id) continue;
        await axios.post(
          `${API_URL}/api/volunteers/${titular.volunteer_id}/family/link`,
          {
            linked_volunteer_id: r.volunteer_id,
            role: r._role,
            relationship: r._relationship,
          },
          { withCredentials: true },
        );
      }
      // Update titular's own relationship/role if changed
      if (titular._relationship !== (titular.family_relationship || '') || titular._role !== (titular.family_role || '')) {
        await axios.put(
          `${API_URL}/api/volunteers/${titular.volunteer_id}/family/role`,
          { role: titular._role, relationship: titular._relationship },
          { withCredentials: true },
        );
      }
      toast.success(lang === 'es' ? 'Familia vinculada' : 'Family linked');
      onSaved();
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al vincular' : 'Link error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!group} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{lang === 'es' ? 'Gestionar familia para' : 'Manage family for'}: {group.email}</DialogTitle>
          <DialogDescription>
            {lang === 'es'
              ? 'Asigna rol y parentesco a cada voluntario y pulsa Vincular para crear/unir la familia.'
              : 'Set role and relationship for each volunteer and click Link to create/join the family.'}
          </DialogDescription>
        </DialogHeader>

        {isConflict && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-800 flex gap-2">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <strong>{lang === 'es' ? 'Conflicto de familia detectado' : 'Family conflict detected'}.</strong>{' '}
              {lang === 'es'
                ? 'Estos voluntarios pertenecen a familias distintas. Si los vinculas a esta nueva familia, mantendran su familia previa y la operacion fallara para algunos con codigo 409. Recomendado: desvincula primero los miembros conflictivos desde sus respectivas fichas individuales.'
                : 'These volunteers belong to different families. Linking them here will fail with 409 for some. Unlink the conflicting members from their individual profiles first.'}
            </div>
          </div>
        )}

        <div className="border border-stone-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 text-stone-600">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">V-code</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Nombre' : 'Name'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Edad' : 'Age'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Familia actual' : 'Current family'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Rol' : 'Role'}</th>
                <th className="text-left font-medium">{lang === 'es' ? 'Parentesco' : 'Relationship'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const age = ageFromDOB(r.fecha_nacimiento);
                return (
                  <tr key={r.volunteer_id} className="border-t border-stone-100">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.volunteer_id}</td>
                    <td className="font-medium">{r.display_name}</td>
                    <td>{age !== null ? age : '-'}</td>
                    <td>
                      {r.family_id ? (
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">{r.family_role}</Badge>
                      ) : <span className="text-stone-400">-</span>}
                    </td>
                    <td>
                      <Select value={r._role} onValueChange={(v) => update(r.volunteer_id, '_role', v)}>
                        <SelectTrigger className="h-7 w-36 text-xs" data-testid={`email-dup-role-${r.volunteer_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="titular" className="text-xs">{lang === 'es' ? 'Titular' : 'Holder'}</SelectItem>
                          <SelectItem value="dependiente" className="text-xs">{lang === 'es' ? 'Dependiente' : 'Dependent'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <Input
                        value={r._relationship}
                        onChange={(e) => update(r.volunteer_id, '_relationship', e.target.value)}
                        placeholder={lang === 'es' ? 'madre, hijo...' : 'mother, son...'}
                        className="h-7 w-44 text-xs"
                        data-testid={`email-dup-relationship-${r.volunteer_id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-stone-500">
          {lang === 'es'
            ? `Se usara el primer titular como base de la familia y se vinculara al resto contra el. ${
              isConflict ? 'Por el conflicto, esta operacion puede fallar parcialmente.' : ''
            }`
            : `The first holder will be used as family base and the rest will link against it. ${
              isConflict ? 'Due to conflict, this operation may fail partially.' : ''
            }`}
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={saving} data-testid="email-dup-confirm-link">
            <LinkIcon size={14} className="mr-1.5" />
            {saving ? '...' : (lang === 'es' ? 'Vincular familia' : 'Link family')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
